/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import { copy } from 'angular';

import {
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    Type,
} from '@angular/core';
import { ClrDatagridStateInterface } from '@clr/angular';
import { Subscription } from 'rxjs';
import { isEmpty, isEqual } from 'underscore';
import { L10nService } from '@vmw/ngx-vip';
import { AviDropdownButtonPosition } from 'ng/shared/components';
import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { DialogService } from 'dialog-service';
import { StringService } from 'string-service';
import { Auth } from 'ajs/modules/core/services/auth';
import * as globalL10n from 'global-l10n';
import { TAviDataGridColumnPresenceMap } from 'nsx-alb-ui-components';
import { IAviDataGridState } from 'ng/root-store/avi-data-grid-store/avi-data-grid-store.state';
import { Store } from '@ngrx/store';
import { selectAviDataGridState } from 'ng/root-store/avi-data-grid-store/avi-data-grid-store.selectors';
import {
    saveAviDataGridDisplayedColumnsSelection,
} from 'ng/root-store/avi-data-grid-store/avi-data-grid-store.actions';
import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from './avi-collection-data-grid.types';

import './avi-collection-data-grid.component.less';

const { ...globalL10nKeys } = globalL10n;

const REFERENCE_CHECK_DIALOG_ID = 'reference-check-dialog';

/**
 * @description
 *
 *     Collection Grid component, built using AviDataGridBase.
 *
 *     This component deals with the collection passed-in to show the Items in the collection as
 *     rows with item editing and creating functionalities.
 *
 * @example
 *
 *     this.webhookCollection = new WebhookCollection();
 *
 *     const { objectName } = this.webhookCollection;
 *
 *     this.webhookCollectionGridConfig = {
 *         id: `${objectName}-list-page`
 *         collection: this.webhookCollection,
 *         fields: [{
 *             id: 'name',
 *             label: l10nService.getMessage(l10nKeys.columnTitleName),
 *             sortBy: 'name',
 *             visibility: AviDataGridFieldVisibility.MANDATORY
 *             transform: row => row.getName(),
 *         }, {
 *             id: 'callback-url',
 *             label: l10nService.getMessage(l10nKeys.columnTitleCallbackUrl),
 *             sortBy: 'callback_url',
 *             transform: row => row.getConfig().callback_url,
 *         }, {
 *             id: 'verfication-token',
 *             label: l10nService.getMessage(l10nKeys.columnTitleVerificationToken),
 *             visibility: AviDataGridFieldVisibility.OPTIONAL,
 *             transform: row => row.getConfig().verification_token,
 *         }],
 *         layout: {
 *             placeholderMessage: 'No VRF context found!',
 *         },
 *         createActions: [{
 *             label: 'Basic Create',
 *             onClick: () => null,
 *         }, {
 *             label: 'Advanced Create',
 *             onClick: () => null,
 *         }],
 *         getUniqueRowId: (row: VRFContext) => row.id,
 *     };
 *
 *     <avi-collection-data-grid
 *         [config]="webhookCollectionGridConfig"
 *     >
 *         <avi-collection-data-grid_header>
 *             <h6>
 *                 Webhook Collection
 *             </h6>
 *         </avi-collection-data-grid_header>
 *     </avi-collection-data-grid>
 *
 * @author Zhiqian Liu
 */
@Component({
    selector: 'avi-collection-data-grid',
    templateUrl: './avi-collection-data-grid.component.html',
})
export class AviCollectionDataGridComponent implements OnInit, OnChanges, DoCheck {
    /**
     * Grid configuration object containing getUniqueRowId, multipleactions, etc.
     */
    @Input()
    public config: IAviCollectionDataGridConfig;

    /**
     * Grid Title sent to the grid.
     */
    @Input()
    public gridTitle?: string;

    /**
     * Fires on row selection change.
     */
    @Output()
    public onSelectionChange = new EventEmitter<IAviCollectionDataGridRow[]>();

    /**
     * Used to decide the true data grid config under the hood by adding essential functionalities
     * on top of the passed-in config.
     */
    public internalConfig: IAviCollectionDataGridConfig;

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Primary position for the dropdown of the create button.
     */
    public createDropdownPrimaryPosition = AviDropdownButtonPosition.BOTTOM_LEFT;

    /**
     * Reference to selected rows. Used to deselect selected rows when the context changes, ex. gridConfig is modified.
     */
    public selectedRows: IAviCollectionDataGridRow[] = [];

    /**
     * Reference to current page. Used to reset the page when the context changes, ex. gridConfig is modified.
     */
    public currentPage = 1;

    /**
     * Getter of the collection bound to the data grid.
     */
    public get collection(): Collection {
        return this.internalConfig.collection;
    }

    /**
     * Show create button / dropdown button only when the collection is creatable and the config
     * doesn't appoint it hidden.
     */
    public get showCreate(): boolean {
        return !this.internalConfig.layout?.hideCreate && this.collection.isCreatable();
    }

    /**
     * Grey out the create button / dropdown button.
     */
    public get disableCreate(): boolean {
        return this.internalConfig.layout?.disableCreate;
    }

    /**
     * Label for the create button. Use a default value if not configured.
     */
    public get createButtonLabel(): string {
        return this.internalConfig.layout?.createButtonLabel ||
            this.l10nService.getMessage(globalL10nKeys.createLabel);
    }

    /**
     * Whether to turn the create button to a menu dropdown.
     */
    public get useDropdownCreate(): boolean {
        return Boolean(this.internalConfig.createActions?.length);
    }

    public columnVisibilityHash: TAviDataGridColumnPresenceMap = {};

    private aviDataGridStateSubscription: Subscription;

    private aviDataGridState: IAviDataGridState;

    /**
     * Latest data grid state object containing info about pagination, sorting and filtering.
     */
    private dataGridState: ClrDatagridStateInterface;

    public constructor(
        private readonly authService: Auth,
        private readonly stringService: StringService,
        private readonly l10nService: L10nService,
        private readonly dialogService: DialogService,
        private readonly elementRef: ElementRef,
        private readonly aviDataGridStore: Store<IAviDataGridState>,
    ) {}

    /** @override */
    public ngOnInit(): void {
        this.setInternalConfigs();
        this.setInitialSorting();
        this.getAviDataGridDisplayedColumnsSelectionState();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { config } = changes;

        if (config && !config.isFirstChange()) {
            this.selectedRows = [];
            this.currentPage = 1;
            this.setInternalConfigs();
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.aviDataGridStateSubscription.unsubscribe();
    }

    /**
     * @override
     * Used to add a custom change detection for properties under this.config and update
     * this.internalConfig. This detection is needed because Angular is not able to detect any
     * changes of those properties if they're not directly bound to the template,
     * and this.internalConfig needs to be updated as the original config changes.
     */
    public ngDoCheck(): void {
        if (this.config?.fields !== this.internalConfig.fields) {
            this.internalConfig.fields = this.config?.fields || [];

            this.setInternalConfigs();
        }

        if (this.config?.expandedContentTemplateRef !==
                this.internalConfig.expandedContentTemplateRef
        ) {
            // can't set in ngAfterViewInit() since it's triggered before the parent afterViewInit
            // sets the expander ref, also the ref can be dynamically changed
            this.setInternalConfigExpanderTemplateRef();
        }
    }

    /**
     * Called on row selection change.
     * Help as a relay to bubble up the onSelectionChange output from avi-data-grid-base to the
     * parent using this avi-collection-data-grid component.
     */
    public onRowSelectionChange(rows: IAviCollectionDataGridRow[]): void {
        this.selectedRows = rows;
        this.onSelectionChange.emit(rows);
    }

    /**
     * Update the local data grid state with the latest one bubbled up from avi-data-grid-base.
     * This method is called page init and everytime afterwards when pagination, sorting, filtering
     * info gets updated. Currently (08/20/21) used to load data based on pagination.
     * TODO: add load by filtering and sorting.
     */
    public updateLocalDataGridState(state: ClrDatagridStateInterface): void {
        this.dataGridState = state;
        this.selectedRows = [];
        this.currentPage = state.page.current;

        this.loadCurrentPage();
    }

    /**
     * Search by field values.
     */
    public search(value: string): void {
        this.collection.search(value?.trim());
    }

    public onColumnVisibilityHashChange(hash: TAviDataGridColumnPresenceMap): void {
        const gridId = this.getGridId();

        if (isEmpty(hash) || isEqual(this.aviDataGridState[gridId]?.gridColumnVisibility, hash)) {
            return;
        }

        const currentGridColumnVisibilityState = { [gridId]: { gridColumnVisibility: { ...hash } } };

        this.aviDataGridStore.dispatch(
            saveAviDataGridDisplayedColumnsSelection({ currentGridColumnVisibilityState }),
        );
    }

    private getGridId(): string {
        const NO_NATIVE_ELEMENT_ID = 'NO_NATIVE_ELEMENT_ID';
        const NO_CONFIG_ID = 'NO_CONFIG_ID';
        const gridId = `${this.config.id ?? NO_CONFIG_ID}__${this.elementRef.nativeElement.id || NO_NATIVE_ELEMENT_ID}`;

        return gridId;
    }

    private getAviDataGridDisplayedColumnsSelectionState(): void {
        const gridId = this.getGridId();

        this.aviDataGridStateSubscription =
            this.aviDataGridStore.select(selectAviDataGridState).subscribe(state => {
                this.aviDataGridState = state;
                this.columnVisibilityHash = { ...state[gridId]?.gridColumnVisibility };
            });
    }

    /**
     * Load collection on current page.
     */
    private loadCurrentPage(): void {
        const { sort, page: { size, current } } = this.dataGridState;

        if (sort) {
            const { by, reverse } = sort;

            const sortBy = reverse ? `-${by}` : by;

            // the type assertion is safe here as for now only sort by field name is supported
            this.collection.setSorting(sortBy as string, true);
        }

        this.collection.loadPage(size * (current - 1), size);
    }

    /**
     * Set the internal configs based on the pass-in one to ensure basic functionalities of a
     * collection grid.
     */
    private setInternalConfigs(): void {
        // get a deep copy of the pass-in config for further settings
        // but we'll save the overhead doing that for the collection
        const {
            collection,
            fields,
            expandedContentTemplateRef, // template refs can't be set under onInit()
            getUniqueRowId,
            ...props
        } = this.config;

        // If not set by parent component, set getUniqueRowId to default.
        const internalGetRowId = getUniqueRowId || ((row: IAviCollectionDataGridRow) => row.id);

        this.internalConfig = {
            collection,
            fields,
            expandedContentTemplateRef,
            getUniqueRowId: internalGetRowId,
            ...copy(props),
        };

        // set basic collection grid operations
        this.setEditSingleAction();
        // TODO AV-183958: Uncomment to enable view mode for users.
        // this.setViewSingleAction();
        this.setRowSelectionDisable();
        this.setDeleteSingleAction();
        this.setDeleteMultipleAction();
        this.addTenantColumn();
        this.setReferenceCheckSingleAction();
    }

    /**
     * Add 'Edit' single action to the config if there isn't one and hideEdit is false.
     */
    private setEditSingleAction(): void {
        if (this.internalConfig.layout?.hideEdit) {
            return;
        }

        if (!this.internalConfig.singleactions) {
            this.internalConfig.singleactions = [];
        }

        const { singleactions } = this.internalConfig;
        const hasEditSingleAction = singleactions.some(({ id }) => {
            const normalizedId = id.toLowerCase();

            return normalizedId === 'edit';
        });

        if (!hasEditSingleAction) {
            singleactions.push({
                id: 'edit',
                label: this.l10nService.getMessage(globalL10nKeys.editLabel),
                shape: 'pencil',
                onClick: (row: IAviCollectionDataGridRow) => row.edit(),
                disabled: (row: IAviCollectionDataGridRow) => !row.isEditable(),
            });
        }
    }

    /**
     * Add the reference check singleaction to the list of grid single actions.
     */
    private setReferenceCheckSingleAction(): void {
        if (this.internalConfig.layout?.hideReferenceCheckAction) {
            return;
        }

        if (!this.internalConfig.singleactions) {
            this.internalConfig.singleactions = [];
        }

        this.internalConfig.singleactions.push({
            id: 'reference-check',
            label: this.l10nService.getMessage(globalL10nKeys.whereUsedLabel),
            shape: 'organization',
            onClick: (row: IAviCollectionDataGridRow) => this.openReferenceCheckModal(row),
        });
    }

    /**
     * Lazy-load the reference check dialog and open it with the dialog service.
     */
    private async openReferenceCheckModal(item: IAviCollectionDataGridRow): Promise<void> {
        const {
            ReferenceCheckDialogComponent,
        } = await import(
            /* webpackChunkName: "reference-check-dialog-modal" */
            'ng/lazy-loaded-components/dialogs/reference-check-dialog'
        );

        this.dialogService.add({
            id: REFERENCE_CHECK_DIALOG_ID,
            component: ReferenceCheckDialogComponent as Type<Component>,
            componentProps: {
                name: item.getName(),
                uuid: item.id,
                objectName: item.getItemType(),
                onClose: () => this.dialogService.remove(REFERENCE_CHECK_DIALOG_ID),
            },
        });
    }

    /**
     * Add 'View' single action to the config if there isn't one and if hideEdit is false.
     */
    private setViewSingleAction(): void {
        if (this.internalConfig.layout?.hideEdit) {
            return;
        }

        if (!this.internalConfig.singleactions) {
            this.internalConfig.singleactions = [];
        }

        const { singleactions } = this.internalConfig;
        const hasViewSingleAction = singleactions.some(({ id }) => {
            const normalizedId = id.toLowerCase();

            return normalizedId === 'view';
        });

        if (!hasViewSingleAction) {
            singleactions.push({
                id: 'view',
                label: this.l10nService.getMessage(globalL10nKeys.viewLabel),
                shape: 'eye',
                onClick: (row: IAviCollectionDataGridRow) => row.openInViewMode(),
                disabled: (row: IAviCollectionDataGridRow) => !row.isViewable(),
            });
        }
    }

    /**
     * Add row selection (checkbox) disabling predicate if there isn't one.
     */
    private setRowSelectionDisable(): void {
        const { rowSelectionDisabled } = this.internalConfig;

        if (!rowSelectionDisabled) {
            this.internalConfig.rowSelectionDisabled =
                (row: IAviCollectionDataGridRow) => !row.isDroppable();
        }
    }

    /**
     * Add 'Delete' single action to the config if there isn't one and hideDelete is false.
     */
    private setDeleteSingleAction(): void {
        if (this.internalConfig.layout?.hideDelete) {
            return;
        }

        if (!this.internalConfig.singleactions) {
            this.internalConfig.singleactions = [];
        }

        const { singleactions } = this.internalConfig;
        const hasDeleteSingleAction = singleactions.some(({ id }) => {
            const normalizedId = id.toLowerCase();

            return normalizedId === 'delete' || normalizedId === 'remove';
        });

        if (!hasDeleteSingleAction) {
            singleactions.push({
                id: 'delete',
                label: this.l10nService.getMessage(globalL10nKeys.deleteLabel),
                shape: 'trash',
                onClick: (row: IAviCollectionDataGridRow) => this.collection.dropItems(row),
                disabled: (row: IAviCollectionDataGridRow) => !row.isDroppable(),
            });
        }
    }

    /**
     * Add 'Delete' multiple action to the config if there isn't one and hideDelete is false.
     */
    private setDeleteMultipleAction(): void {
        if (this.internalConfig.layout?.hideDelete) {
            return;
        }

        if (!this.internalConfig.multipleactions) {
            this.internalConfig.multipleactions = [];
        }

        const { multipleactions } = this.internalConfig;
        const hasDeleteMultipleAction = multipleactions.some(({ id }) => {
            const normalizedId = id.toLowerCase();

            return normalizedId === 'delete' || normalizedId === 'remove';
        });

        if (!hasDeleteMultipleAction) {
            multipleactions.push({
                id: 'delete',
                label: this.l10nService.getMessage(globalL10nKeys.deleteLabel),
                onClick: (rows: IAviCollectionDataGridRow[]) => {
                    const rowsToDelete = rows.filter(row => row.isDroppable());

                    this.collection.dropItems(rowsToDelete);
                },
                // `selected` from `avi-data-grid-base` (`rows` here) is undefined when
                // `hideCheckboxes` is set to true
                disabled: (rows: IAviCollectionDataGridRow[]) => rows?.every(
                    row => !row.isDroppable(),
                ),
            });
        }
    }

    /**
     * Add a "Tenant" column to the grid under "All Tenants" mode.
     */
    private addTenantColumn(): void {
        if (
            this.authService.allTenantsMode() &&
                !this.internalConfig.layout?.hideTenantColumn &&
                    this.internalConfig.fields.length > 0
        ) {
            this.internalConfig.fields.push({
                label: this.l10nService.getMessage(globalL10nKeys.tenantLabel),
                id: 'tenant',
                transform: row => this.stringService.name(row.getTenantRef()) ||
                    row.getConfig().tenant,
            });
        }
    }

    /**
     * Set collection data grid's initial sorting order with the default one; either provided by the
     * config or from data source default which usually sorts by 'name'.
     */
    private setInitialSorting(): void {
        const { collection, defaultSorting } = this.internalConfig;

        if (defaultSorting && defaultSorting !== collection.getSorting()) {
            collection.setSorting(defaultSorting);
        }
    }

    /**
     * Set the template ref for expander in the internal config.
     */
    private setInternalConfigExpanderTemplateRef(): void {
        const { expandedContentTemplateRef } = this.config;

        this.internalConfig.expandedContentTemplateRef = expandedContentTemplateRef;
    }
}
